import React from "react";
import { observer } from "mobx-react";
import { visualizationStore } from "../../stores/VisualizationStore";
import { widgetSpecToHtml } from "./widget/widgetSpecToHtml";
import { customWidgetsEnabled } from "../../lib/customWidgets";

const KIND_ICON = { plotly: "📊", network: "🌐", molecule: "🧬", widget: "🧩", sar: "🧪" };

export const CanvasTabStrip = observer(({ onClose, onRegenerate, sourceReferencesStore }) => {
  const charts = Array.from(visualizationStore.charts.entries())
    .filter(([id]) => !visualizationStore.hiddenChartIds.has(id));
  const pendingEntries = visualizationStore.pendingWidgetEntries;
  const activeChartId = visualizationStore.activeChartId;
  const activeTab = visualizationStore.activeTab;
  const sourcesCount = sourceReferencesStore?.dedupedSources?.length || 0;

  // The active chart entry (real charts only — ghost/pending have no HTML to download).
  const activeChartEntry =
    activeTab === "chart" && activeChartId && !activeChartId.startsWith("pending:")
      ? visualizationStore.charts.get(activeChartId)
      : null;
  // Only offer the standalone-HTML export for widgets the account is entitled
  // to render (PLEX-4237) — a gated-off widget shows a placeholder, not HTML.
  const isActiveWidget = activeChartEntry?.kind === "widget" && customWidgetsEnabled();

  const handleDownloadHtml = () => {
    if (!activeChartEntry) return;
    try {
      const { html, filename } = widgetSpecToHtml(activeChartEntry.spec, activeChartEntry.title);
      const blob = new Blob([html], { type: "text/html;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (_) {
      // silently ignore if composeWidgetDocument fails (e.g., bad spec)
    }
  };

  return (
    <header className="canvas-tab-strip" role="tablist" aria-label="Canvas tabs">
      <div className="canvas-tabs-scroll">
        {charts.map(([id, payload], idx) => {
          const isActive = activeTab === "chart" && activeChartId === id;
          const icon = KIND_ICON[payload.kind] || "📊";
          const title = payload.title || `Chart ${idx + 1}`;
          return (
            <button
              key={id}
              role="tab"
              aria-selected={isActive}
              data-tab="chart"
              data-chart-id={id}
              className={`canvas-tab${isActive ? ' active' : ''}`}
              title={title}
              onClick={() => visualizationStore.revealChart(id)}
            >
              <span className="tab-icon" aria-hidden="true">{icon}</span>
              <span className="tab-label">{title}</span>
              <span
                role="button"
                aria-label={`Close ${title}`}
                tabIndex={-1}
                className="tab-close-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  visualizationStore.removeChart(id);
                }}
              >✕</span>
            </button>
          );
        })}
        {pendingEntries.map((entry) => {
          const isActive = activeTab === "chart" && activeChartId === entry.chartId;
          return (
            <button
              key={entry.chartId}
              role="tab"
              aria-selected={isActive}
              data-tab="chart"
              data-chart-id={entry.chartId}
              data-streaming="true"
              className={`canvas-tab canvas-tab--streaming${isActive ? ' active' : ''}`}
              title={entry.title}
              onClick={() => visualizationStore.setActivePending(entry.toolUseId)}
            >
              <span className="tab-icon" aria-hidden="true">🧩</span>
              <span className="tab-label tab-label--streaming">{entry.title}</span>
            </button>
          );
        })}
        <button
          role="tab"
          aria-selected={activeTab === "sources"}
          data-tab="sources"
          className={`canvas-tab canvas-tab-sources${activeTab === "sources" ? ' active' : ''}${sourcesCount === 0 ? ' empty' : ''}`}
          onClick={() => visualizationStore.openSourcesTab()}
        >
          <span className="tab-icon" aria-hidden="true">🔗</span>
          <span className="tab-label">Sources</span>
          <span className="tab-badge">{sourcesCount}</span>
        </button>
      </div>
      <div className="canvas-actions">
        {isActiveWidget && (
          <button type="button" aria-label="Download HTML" title="Download widget as standalone HTML" onClick={handleDownloadHtml}>⤓ Download HTML</button>
        )}
        {activeTab === "chart" && (
          <button type="button" aria-label="Regenerate" title="Regenerate this chart" onClick={() => onRegenerate?.(activeChartId)}>↻</button>
        )}
        <button type="button" aria-label="Close canvas" title="Hide canvas (charts kept)" onClick={onClose}>✕</button>
      </div>
    </header>
  );
});
